import React, { useEffect, useState } from "react";
import logo from "../assets/image/eblackdog_logo.png";

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || "https://rfs.react.eblackdog.net/api";

// SVG Icons
const IconUser = () => (
  <svg className="w-[17px] h-[17px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="8" r="4" />
    <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
  </svg>
);

const IconLock = () => (
  <svg className="w-[17px] h-[17px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

const IconEyeOpen = () => (
  <svg className="w-[17px] h-[17px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const IconEyeClosed = () => (
  <svg className="w-[17px] h-[17px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

const IconPaw = () => (
  <svg className="w-[13px] h-[13px]" viewBox="0 0 24 24" fill="currentColor">
    <ellipse cx="6" cy="6" rx="2.5" ry="3" />
    <ellipse cx="12" cy="4" rx="2.5" ry="3" />
    <ellipse cx="18" cy="6" rx="2.5" ry="3" />
    <ellipse cx="3.5" cy="12" rx="2" ry="2.5" />
    <path d="M12 22c-4 0-8-3-8-7 0-2 1.5-3.5 3-4.5 1-.7 2.5-1.5 5-1.5s4 .8 5 1.5c1.5 1 3 2.5 3 4.5 0 4-4 7-8 7z" />
  </svg>
);

const IconCheck = () => (
  <svg className="w-[15px] h-[15px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const IconAlert = () => (
  <svg className="w-[15px] h-[15px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

const IconInfo = () => (
  <svg className="w-[15px] h-[15px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="16" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);

export default function GuardLogin() {
  const [formData, setFormData] = useState({
    username: "",
    password: "",
    loginType: "sentinel",
    remember: false,
  });

  const [showPassword, setShowPassword] = useState(false);
  const [status, setStatus] = useState({
    visible: false,
    message: "",
    type: "info",
  });
  const [loading, setLoading] = useState(false);

  const showStatus = (message, type = "info") => {
    setStatus({
      visible: true,
      message,
      type,
    });
  };

  useEffect(() => {
    const rememberedUsername = localStorage.getItem("guard_remembered_username");
    const rememberedType = localStorage.getItem("guard_remembered_type");

    if (rememberedUsername) {
      setFormData((prev) => ({
        ...prev,
        username: rememberedUsername,
        loginType: rememberedType || "sentinel",
        remember: true,
      }));
    }
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const clearOldAdminLoginData = () => {
    [
      "company_name",
      "licensedFor",
      "admin_active",
      "company_address",
      "company_phone",
      "admin_email",
      "company_logo",
      "user_map",
    ].forEach((key) => localStorage.removeItem(key));
  };

  const startGpsOnLogin = async (token) => {
    if (!navigator.geolocation) return;

    const position = await new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        resolve,
        () => resolve(null),
        {
          enableHighAccuracy: true,
          timeout: 8000,
          maximumAge: 0,
        }
      );
    });

    if (!position?.coords) return;

    const latitude = position.coords.latitude;
    const longitude = position.coords.longitude;
    const accuracy = position.coords.accuracy || 0;

    if (
      Number.isNaN(latitude) ||
      Number.isNaN(longitude) ||
      latitude < -90 ||
      latitude > 90 ||
      longitude < -180 ||
      longitude > 180
    ) {
      return;
    }

    try {
      const res = await fetch(`${API_BASE_URL}/gps`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          action: "ensure",
          latitude,
          longitude,
          accuracy,
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (data?.success && data?.tracking_id) {
        sessionStorage.setItem("gps_tracking_id", String(data.tracking_id));
      }
    } catch (error) {
      console.error("[GuardLogin] GPS start error:", error);
    }
  };

const handleSubmit = async (e) => {
  e.preventDefault();

  const username = formData.username.trim();
  const password = formData.password.trim();

  if (!username || !password) {
    showStatus("Please enter both username and password", "error");
    return;
  }

  setLoading(true);
  showStatus("Authenticating guard credentials...", "info");

  try {
    const res = await fetch(`${API_BASE_URL}/guard-login`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        username,
        password,
        loginType: formData.loginType,
      }),
    });

    const result = await res.json().catch(() => ({}));

    if (!res.ok || !result.success) {
      showStatus(result.message || "Guard login failed", "error");
      setLoading(false);
      return;
    }

    const token = result.token;
    const rawGuard = result.data || result.guard || result.user || {};

    if (!token) {
      showStatus("Login success but token missing from backend", "error");
      setLoading(false);
      return;
    }

    if (!result.data && !result.guard && !result.user) {
      console.warn("[GuardLogin] Backend returned no guard profile object — check API response shape.");
    }

    const memberId =
      rawGuard.memberId ||
      rawGuard.member_id ||
      rawGuard.fld_memberId ||
      rawGuard.guard_id ||
      username;

    const adminId =
      rawGuard.adminId ||
      rawGuard.admin_id ||
      rawGuard.created_by_admin_id ||
      rawGuard.createdByAdminId ||
      rawGuard.created_by ||
      null;

    const designation =
      rawGuard.designation ||
      rawGuard.fld_designation ||
      (formData.loginType === "patrol" ? "Patrol Guard" : "Sentinel Guard");

    const guardAddress =
      rawGuard.address ||
      rawGuard.fld_address ||
      rawGuard.fld_current_address ||
      rawGuard.current_address ||
      "";

    const guardPhone =
      rawGuard.phone ||
      rawGuard.mobile ||
      rawGuard.fld_mobile ||
      rawGuard.fld_phone ||
      rawGuard.contact_number ||
      "";

    const guardInfo = {
      ...rawGuard,
      id: memberId,
      memberId,
      adminId,
      role: designation,
      designation,
      address: guardAddress,
      phone: guardPhone,
      loginType: formData.loginType,
      guardLoginType: formData.loginType,
    };

    if (formData.remember) {
      localStorage.setItem("guard_remembered_username", username);
      localStorage.setItem("guard_remembered_type", formData.loginType);
    } else {
      localStorage.removeItem("guard_remembered_username");
      localStorage.removeItem("guard_remembered_type");
    }

    clearOldAdminLoginData();

    localStorage.setItem("token", token);
    localStorage.setItem("authToken", token);
    localStorage.setItem("guardToken", token);
    localStorage.setItem("isLoggedIn", "true");
    localStorage.setItem("isGuardLoggedIn", "true");
    localStorage.setItem("loginType", formData.loginType);
    localStorage.setItem("guardLoginType", formData.loginType);
    localStorage.setItem("role", designation);
    localStorage.setItem("designation", designation);
    localStorage.setItem("userData", JSON.stringify(guardInfo));
    localStorage.setItem("guardData", JSON.stringify(guardInfo));
    localStorage.setItem("memberId", memberId || "");
    localStorage.setItem("adminId", adminId || "");
    localStorage.setItem("guardName", guardInfo.fullName || guardInfo.name || "");
    localStorage.setItem("guardEmail", guardInfo.email || "");
    localStorage.setItem("guardAddress", guardAddress);
    localStorage.setItem("guardPhone", guardPhone);

    showStatus("Login successful! Getting location...", "success");

    try {
      await Promise.race([
        startGpsOnLogin(token),
        new Promise((resolve) => setTimeout(resolve, 3000)),
      ]);
    } catch (gpsError) {
      console.error("[GuardLogin] GPS race error:", gpsError);
    }

    window.location.replace("/dashboard");
  } catch (error) {
    console.error("[GuardLogin] Login error:", error);
    showStatus("Network error. Please try again.", "error");
    setLoading(false);
  }
};
  const statusConfig = {
    success: { cls: "border-emerald-500/20 bg-emerald-500/10 text-emerald-400", icon: <IconCheck /> },
    error: { cls: "border-red-500/20 bg-red-500/10 text-red-400", icon: <IconAlert /> },
    info: { cls: "border-blue-500/20 bg-blue-500/10 text-blue-400", icon: <IconInfo /> },
  };

  const { cls: statusCls, icon: statusIcon } =
    statusConfig[status.type] || statusConfig.info;

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-6 font-['Inter',system-ui,sans-serif] relative overflow-hidden">
      {/* Background glow effects */}
      <div className="absolute top-[-200px] left-[-200px] w-[600px] h-[600px] rounded-full bg-gradient-radial from-green-500/10 to-transparent pointer-events-none" />
      <div className="absolute bottom-[-200px] right-[-200px] w-[500px] h-[500px] rounded-full bg-gradient-radial from-green-500/5 to-transparent pointer-events-none" />

      {/* Main Card */}
      <div className="relative z-10 w-full max-w-[420px] bg-[#141414] rounded-2xl overflow-hidden border border-white/5 shadow-[0_50px_120px_rgba(0,0,0,0.8)] shadow-[0_0_0_1px_rgba(255,193,7,0.06)]">
        
        {/* Header */}
        <div className="bg-gradient-to-b from-[#1a1a1a] to-[#141414] px-10 py-10 text-center border-b border-white/5">
          <div className="inline-flex items-center gap-2 bg-green-500/10 border border-green-500/20 text-green-500 text-[10px] font-bold tracking-[0.15em] uppercase px-3.5 py-1.5 rounded-full mb-5">
            <IconPaw />
            Guard Access Portal
          </div>

          <div className="w-20 h-20 mx-auto mb-4 rounded-full p-1 bg-gradient-to-br from-green-500 to-green-600 shadow-[0_0_30px_rgba(255,193,7,0.15)]">
            <div className="w-full h-full rounded-full bg-[#141414] p-1.5 overflow-hidden">
              <img src={logo} alt="eBLACKDOG Logo" className="w-full h-full rounded-full object-cover" />
            </div>
          </div>

          <h1 className="text-white text-[22px] font-extrabold tracking-[-0.02em]">
            Guard Sign In
          </h1>
          <p className="text-gray-500 text-[13px] mt-1 font-normal">
            Login to your patrol or sentinel account
          </p>
        </div>

        {/* Body */}
        <div className="px-10 py-8">
          <form onSubmit={handleSubmit}>
            {/* Login Type Selector */}
            <div className="mb-4">
              <label className="block text-[11px] font-bold tracking-[0.08em] uppercase text-gray-400 mb-1.5">
                Login Type
              </label>
              <div className="grid grid-cols-2 gap-2.5">
                <label
                  className={`p-2.5 rounded-lg border-[1.5px] text-center cursor-pointer transition-all duration-200 text-[13px] font-semibold ${
                    formData.loginType === "sentinel"
                      ? "border-green-500 bg-green-500/10 text-green-500 shadow-[0_0_20px_rgba(255,193,7,0.05)]"
                      : "border-white/5 bg-[#0d0d0d] text-gray-500 hover:border-green-500/20 hover:bg-green-500/5"
                  }`}
                >
                  <input
                    type="radio"
                    name="loginType"
                    value="sentinel"
                    checked={formData.loginType === "sentinel"}
                    onChange={handleChange}
                    className="hidden"
                  />
                  🛡️ Sentinel
                </label>
                <label
                  className={`p-2.5 rounded-lg border-[1.5px] text-center cursor-pointer transition-all duration-200 text-[13px] font-semibold ${
                    formData.loginType === "patrol"
                      ? "border-green-500 bg-green-500/10 text-green-500 shadow-[0_0_20px_rgba(255,193,7,0.05)]"
                      : "border-white/5 bg-[#0d0d0d] text-gray-500 hover:border-green-500/20 hover:bg-green-500/5"
                  }`}
                >
                  <input
                    type="radio"
                    name="loginType"
                    value="patrol"
                    checked={formData.loginType === "patrol"}
                    onChange={handleChange}
                    className="hidden"
                  />
                  🚓 Patrol
                </label>
              </div>
            </div>

            {/* Username Field */}
            <div className="mb-4">
              <label htmlFor="username" className="block text-[11px] font-bold tracking-[0.08em] uppercase text-gray-400 mb-1.5">
                Username / Member ID
              </label>
              <div className="relative">
                <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-green-500/70">
                  <IconUser />
                </span>
                <input
                  type="text"
                  id="username"
                  name="username"
                  placeholder="Enter your username or member ID"
                  autoComplete="username"
                  value={formData.username}
                  onChange={handleChange}
                  required
                  className="w-full bg-[#0d0d0d] border-[1.5px] border-white/5 rounded-xl py-3.5 pl-11 pr-4 text-[14px] font-medium text-gray-100 placeholder:text-gray-500 outline-none transition-all duration-200 caret-green-500 focus:border-green-500 focus:shadow-[0_0_0_4px_rgba(255,193,7,0.06)] focus:shadow-[0_0_0_1px_#FFC107] focus:bg-[#111111]"
                />
              </div>
            </div>

            {/* Password Field */}
            <div className="mb-4">
              <label htmlFor="password" className="block text-[11px] font-bold tracking-[0.08em] uppercase text-gray-400 mb-1.5">
                Password
              </label>
              <div className="relative">
                <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-green-500/70">
                  <IconLock />
                </span>
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  name="password"
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  className="w-full bg-[#0d0d0d] border-[1.5px] border-white/5 rounded-xl py-3.5 pl-11 pr-12 text-[14px] font-medium text-gray-100 placeholder:text-gray-500 outline-none transition-all duration-200 caret-green-500 focus:border-green-500 focus:shadow-[0_0_0_4px_rgba(255,193,7,0.06)] focus:shadow-[0_0_0_1px_#FFC107] focus:bg-[#111111]"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-500 hover:text-green-500 transition-colors duration-150 p-1 rounded-md"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <IconEyeClosed /> : <IconEyeOpen />}
                </button>
              </div>
            </div>

            {/* Options */}
            <div className="flex items-center justify-between flex-wrap gap-2.5 my-5">
              <label className="flex items-center gap-2 text-[13px] font-medium text-gray-400 cursor-pointer select-none">
                <input
                  type="checkbox"
                  name="remember"
                  checked={formData.remember}
                  onChange={handleChange}
                  className="w-4 h-4 accent-green-500 cursor-pointer rounded"
                />
                Remember me
              </label>
              <a href="#" className="text-[13px] font-semibold text-green-500 hover:text-green-400 transition-colors duration-150">
                Forgot Password?
              </a>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-br from-green-500 to-green-600 text-[#0a0a0a] text-[14px] font-bold tracking-[0.04em] py-3.5 px-5 rounded-xl border-none cursor-pointer font-['Inter',system-ui,sans-serif] flex items-center justify-center gap-2.5 transition-all duration-200 shadow-[0_4px_20px_rgba(255,193,7,0.2)] hover:shadow-[0_8px_30px_rgba(255,193,7,0.35)] hover:from-green-400 hover:to-green-500 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading && (
                <span className="w-[18px] h-[18px] border-[2.5px] border-black/15 border-t-black rounded-full animate-spin flex-shrink-0" />
              )}
              {loading ? "Authenticating..." : "Sign In as Guard"}
            </button>
          </form>

          {/* Status Message */}
          {status.visible && (
            <div className={`flex items-center gap-2.5 mt-4 p-3 rounded-xl border text-[13px] font-medium leading-[1.4] animate-[fadeSlide_0.3s_ease] ${statusCls}`}>
              <span className="flex items-center flex-shrink-0">{statusIcon}</span>
              <span>{status.message}</span>
            </div>
          )}

          {/* Divider */}
          <div className="flex items-center gap-3 mt-7">
            <div className="flex-1 h-px bg-white/5" />
            <span className="text-[10px] font-semibold tracking-[0.08em] uppercase text-gray-500">
              Need Help?
            </span>
            <div className="flex-1 h-px bg-white/5" />
          </div>
        </div>

        {/* Footer */}
        <div className="px-10 py-5 text-center border-t border-white/5">
          <div className="flex flex-col items-center gap-1 mb-3.5">
            <a href="mailto:eblackdogsupport@nissiinfotech.in" className="text-xs font-medium text-gray-500 hover:text-green-500 transition-colors duration-150">
              eblackdogsupport@nissiinfotech.in
            </a>
            <a href="https://www.eblackdog.com" target="_blank" rel="noreferrer" className="text-xs font-medium text-gray-500 hover:text-green-500 transition-colors duration-150">
              www.eblackdog.com
            </a>
            <span className="text-xs font-semibold tracking-[0.02em] text-gray-500">
              90037-44644 / 98422-44488
            </span>
          </div>
          <div className="flex items-center justify-center gap-2 text-[10.5px] text-gray-600">
            <span className="w-1 h-1 rounded-full bg-green-500/50" />
            <span>© {new Date().getFullYear()} eBLACKDOG Patrol Management System</span>
            <span className="w-1 h-1 rounded-full bg-green-500/50" />
          </div>
        </div>
      </div>

      {/* Custom Animation Keyframes */}
      <style>{`
        @keyframes fadeSlide {
          from {
            opacity: 0;
            transform: translateY(-8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .bg-gradient-radial {
          background-image: radial-gradient(circle, var(--tw-gradient-stops));
        }
      `}</style>
    </div>
  );
}